import React, { useState } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { useUppy } from '@lyearn/shared/hooks/useUppy';
import { cn } from '@lyearn/style';
import { BottomSheet, Box, Button, Typography } from '@lyearn/ui';
import { Modal } from '@/components/Modals';
import { getAppliedTheme } from '@/hooks/useAppColorScheme';
import { avatars } from '@/modules/session/utils/getRandomAvatar';

import UploadImage from '../UploadImage';
import SingleAvatar from './SingleAvatar';
import { actionBtnsContainer, drawer, drawerRoot, fixedSecondaryButtonStyles, w370 } from './style';

interface AvatarSelectorProps {
  isVisible: boolean;
  setVisibility: (visibility: boolean) => void;
  onChange: (avatarUrl: string) => void;
  value: string;
  confirmButtonLabel?: string;
}

function AvatarSelector(props: AvatarSelectorProps) {
  const { t } = useTranslation();
  const { isVisible, setVisibility, onChange, value, confirmButtonLabel } = props;
  const [selectedAvatar, selectAvatar] = useState(value);
  const { isDesktop, isTablet } = useScreen();
  const { files, uppy, clearAll } = useUppy({ referenceId: 'profile-avatar' });

  const onSubmit = () => {
    onChange(selectedAvatar);
    setVisibility(false);
    clearAll();
  };
  const onClose = () => {
    setVisibility(false);
    selectAvatar(value);
    clearAll();
  };

  const allAvatars = avatars.map((avatar) => (
    <SingleAvatar
      key={avatar.url}
      selectAvatar={selectAvatar}
      selectedAvatar={selectedAvatar}
      url={avatar.url}
    />
  ));

  if (isDesktop || isTablet) {
    return (
      <Modal open={isVisible} onClose={onClose}>
        <Box className="relative pt-48 pb-0">
          <Typography className="px-32 text-center" variant="display-xxl">
            {t('Classroom:chooseAvatar')}
          </Typography>
          <Typography
            as="p"
            className={cn('mx-auto mb-40 mt-14 text-center', w370)}
            color="text-text-secondary"
            variant="body-short-01">
            {t('Classroom:chooseAvatarDesc')}
          </Typography>
          <Box className="mb-48 grid grid-cols-4 gap-12 px-32">
            <UploadImage
              files={files}
              selectAvatar={selectAvatar}
              selectedAvatar={selectedAvatar}
              uppy={uppy}
            />
            {allAvatars}
          </Box>
          <Box
            className={cn(
              'sticky bottom-0 z-30 flex w-full p-32 space-s-8',
              getAppliedTheme() === 'light' ? actionBtnsContainer : '',
            )}>
            <Button
              fullWidth
              appearance="secondary"
              className={fixedSecondaryButtonStyles}
              size="large"
              variant="outlined"
              onClick={onClose}>
              {t('Common:Actions.Back')}
            </Button>
            <Button fullWidth size="large" onClick={onSubmit}>
              {confirmButtonLabel || t('Classroom:thatsMe')}
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }

  return (
    <BottomSheet
      className={drawerRoot}
      open={isVisible}
      onClose={onClose}
      onOpen={() => setVisibility(true)}>
      <Box className={cn('w-full pb-20 pt-40', drawer)}>
        <Typography as="p" className="mb-8 px-20" variant="heading-l">
          {t('Classroom:chooseAvatar')}
        </Typography>
        <Typography className="mb-40 px-20" color="text-text-secondary" variant="body-short-01">
          {t('Classroom:chooseAvatarDesc')}
        </Typography>
        <Box className="relative mb-48 grid auto-rows-fr grid-cols-3 gap-16 px-20">
          <UploadImage
            files={files}
            selectAvatar={selectAvatar}
            selectedAvatar={selectedAvatar}
            uppy={uppy}
          />
          {allAvatars}
        </Box>
        <Box
          className={cn(
            'sticky bottom-0 z-30 flex w-full p-20',
            getAppliedTheme() === 'light' ? actionBtnsContainer : '',
          )}>
          <Button fullWidth size="large" onClick={onSubmit}>
            {t('Classroom:thatsMe')}
          </Button>
        </Box>
      </Box>
    </BottomSheet>
  );
}

export default AvatarSelector;
